import {
  menu,
  popFilterableSimpleMenu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import {
  isEditableKeyboardEventTarget,
  streamLainAnimationController,
} from '@blocksuite/affine-shared/utils';
import { type InsertToPosition } from '@blocksuite/affine-shared/utils';
import { CopyIcon, PasteIcon } from '@blocksuite/icons/lit';
import { computed, effect, signal } from '@preact/signals-core';
import { addDays } from 'date-fns/addDays';
import { addMinutes } from 'date-fns/addMinutes';
import { addMonths } from 'date-fns/addMonths';
import { addWeeks } from 'date-fns/addWeeks';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { endOfDay } from 'date-fns/endOfDay';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { isSameDay } from 'date-fns/isSameDay';
import { isSameMonth } from 'date-fns/isSameMonth';
import { startOfDay } from 'date-fns/startOfDay';
import { startOfWeek } from 'date-fns/startOfWeek';
import { html, nothing, type TemplateResult } from 'lit';

import {
  CanvasViewportParticipantController,
  type CanvasViewportTargetPosition,
  createElementCanvasViewportTarget,
} from '../../../core/canvas-viewport.js';
import type {
  DatabaseProjectionPredicate,
  DatabaseProjectionResult,
} from '../../../core/data-source/projection.js';
import { type DataViewI18nKey, dataViewI18nT } from '../../../core/i18n.js';
import { createUniComponentFromWebComponent } from '../../../core/index.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
  dispatchDatabaseLiveViewSetupStateChange,
} from '../../../core/view/data-view-base.js';
import {
  type DataViewBodyState,
  dataViewBodyStateValue,
  getDataViewBodyElement,
  measureDataViewBodyIntrinsicHeight,
} from '../../../core/view/data-view-body.js';
import {
  getSetupWizardBodyIntrinsicHeight,
  renderSetupWizardBodyContent,
} from '../../shared/setup-wizard/setup-wizard-body.js';
import { setupWizardBodyHostStyle } from '../../shared/setup-wizard/setup-wizard-css.js';
import {
  type CalendarOccurrence,
  expandCalendarOccurrences,
  parseCalendarAttendees,
  validateCalendarRecurrenceRule,
} from '../calendar-domain.js';
import type { CalendarSingleView } from '../calendar-view-manager.js';
import { dateAtDayMinute } from '../core/calendar-time-math.js';
import { timedEventGeometry } from '../layout/calendar-event-geometry.js';
import type { CalendarViewSelectionWithType } from '../selection.js';
import {
  CALENDAR_FUNCTION_IDS,
  type CalendarFunctionId,
  renderCalendarChrome,
} from './calendar-chrome-contract.js';
import { animateCalendarSourcePopup } from './calendar-chrome-motion.js';
import {
  CalendarCommandError,
  CalendarCommandSession,
} from './calendar-command-session.js';
import {
  CALENDAR_DETAIL_CLOSE_EVENT,
  CALENDAR_DETAIL_DELETE_EVENT,
  CALENDAR_DETAIL_TARGET_EVENT,
  type CalendarDetailDeleteRequest,
  type CalendarDetailTarget,
} from './calendar-detail-host.js';
import {
  CALENDAR_NEW_EVENT_MINUTES,
  CALENDAR_TIMED_EVENT_MINUTES,
  displayCalendarId,
  hydrateCalendarEventDetails,
  readCalendarEvent,
  readCalendarProjectedEvents,
  writeCalendarCellValue,
} from './calendar-event-store.js';
import { calendarEventHasMailRecipients } from './calendar-mail-accounts.js';
import {
  type CalendarMailboxLink,
  calendarMailboxSendEligibility,
  hasActiveOutboundMailboxLink,
  loadCalendarMailboxLinks,
} from './calendar-mailbox-links.js';
import { CalendarPeriodMotion } from './calendar-period-motion.js';
import {
  resolveCalendarReconcileService,
  subscribeCalendarBackendAvailable,
} from './calendar-reconcile-provider.js';
import { openCalendarSourceOverlays } from './calendar-source-overlays.js';
import {
  openCalendarSourceRename,
  openCalendarSourceTool,
  openCalendarSourceViews,
} from './calendar-source-tools.js';
import {
  beginCalendarSurfaceRangeLoad,
  CALENDAR_SURFACE_HOUR_HEIGHT,
  calendarSurfaceDays,
  calendarSurfaceMonthAnchorKey,
  calendarSurfaceScrollCursor,
  createCalendarSurfaceRange,
  DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE,
  renderCalendarSurface,
  restoreCalendarSurfaceRangeScroll,
} from './calendar-surface.js';
import { styles } from './calendar-view-styles.js';
import type {
  CalendarDataSourceLike,
  CalendarDragOffset,
  CalendarEvent,
  CalendarEventEditPreview,
  CalendarRangeLoading,
  CalendarReconcileSnapshot,
  CalendarReconcileState,
  CalendarSlotDrag,
  CalendarSubscriptionState,
} from './calendar-view-types.js';
import { parseCalendarCursorTime } from './calendar-view-utils.js';
import { renderCalendarDetails } from './detail-panel/calendar-detail-renderer.js';
import {
  CALENDAR_DETAIL_DEFAULT_WIDTH,
  CalendarDetailShellController,
} from './detail-panel/calendar-detail-shell.js';
import {
  type CalendarMountMode,
  detectCalendarMountMode,
} from './edgeless-calendar-view.js';
import {
  CALENDAR_ALL_DAY_RENDER_PAGE_SIZE,
  type CalendarGridRenderContext,
  type CalendarTimedRenderWindow,
  calendarTimedViewport,
  nextCalendarAllDayRenderLimit,
} from './grids/calendar-grid-renderer.js';
import {
  type CalendarIcsActionsContext,
  downloadCalendarEventIcs,
} from './ics/calendar-ics-actions.js';
import {
  downloadCalendarIcs,
  renderCalendarEventsIcs,
  submitCalendarReconcile,
} from './ics/calendar-ics-service.js';
import {
  calendarReconcileColumns,
  flushCalendarReconcileDocs,
  resolveCalendarReconcileContext,
  toCalendarReconcileSnapshot,
} from './ics/calendar-reconcile-service.js';
import { handleCalendarDeleteKey } from './interaction/calendar-delete-key.js';
import {
  calendarClipboardHasEvent,
  copyCalendarOccurrence,
  cutCalendarOccurrence,
  pasteCalendarOccurrence,
} from './interaction/calendar-event-clipboard.js';
import {
  type CalendarEventInteractionContext,
  cancelCalendarOccurrence,
  detachCalendarOccurrence,
  handleCalendarEventKeyDown,
  moveCalendarOccurrenceToDay,
  startCalendarOccurrenceResizePointer,
} from './interaction/calendar-event-interactions.js';
import {
  CALENDAR_SETUP_WIZARD_LAYOUT_CHANGE_EVENT,
  type CalendarWizardStrings,
} from './setup-wizard/calendar-setup-wizard.js';
import { renderCalendarIcsFeedForm } from './toolbar/calendar-toolbar.js';

export { collectCalendarReconcileDocIds } from '../core/calendar-reconcile-contract.js';

const CALENDAR_ALL_DAY_BAND_COLLAPSED_HEIGHT = 32;
const CALENDAR_ALL_DAY_BAND_HEIGHT = 44;
const CALENDAR_EDGELESS_INTRINSIC_HEIGHT_GUARD = 16;
const CALENDAR_EMPTY_STATE_INTRINSIC_HEIGHT = 240;
const CALENDAR_MONTH_CELL_HEIGHT = 122;
const CALENDAR_DAY_MINUTES = 24 * 60;
const CALENDAR_WEEK_HEADER_HEIGHT = 58;
const CALENDAR_TIMED_EVENT_PX_PER_MINUTE = CALENDAR_SURFACE_HOUR_HEIGHT / 60;
const CALENDAR_CANVAS_WEEK_SCROLL_TARGET = 'calendar-week-scroll';
const CALENDAR_CANVAS_MONTH_SCROLL_TARGET = 'calendar-month-scroll';
const CALENDAR_CANVAS_TIME_SCROLL_TARGET = 'calendar-time-scroll';
const CALENDAR_TIMED_RENDER_OVERSCAN_MINUTES = 60;

const calendarWeekStartsOn = { weekStartsOn: 1 } as const;
const initialCalendarSurfaceRange = createCalendarSurfaceRange();

type CalendarTimedOffscreenCounts = Readonly<{ bottom: number; top: number }>;

function calendarElementOffsetWithinAncestor(
  element: HTMLElement,
  ancestor: HTMLElement
): number {
  let offset = 0;
  let current: HTMLElement | null = element;
  while (current && current !== ancestor) {
    offset += current.offsetTop;
    const next = current.offsetParent as HTMLElement | null;
    if (!next || !ancestor.contains(next)) break;
    current = next;
  }
  return offset;
}

const INITIAL_CALENDAR_TIMED_RENDER_WINDOW: CalendarTimedRenderWindow = {
  endMinute: Math.min(
    CALENDAR_DAY_MINUTES,
    DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE + 12 * 60
  ),
  startMinute: Math.max(
    0,
    DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE -
      CALENDAR_TIMED_RENDER_OVERSCAN_MINUTES
  ),
};

function snapCalendarTimedStartMinute(startMinute: number): number {
  return (
    Math.round(startMinute / CALENDAR_TIMED_EVENT_MINUTES) *
    CALENDAR_TIMED_EVENT_MINUTES
  );
}

export function calendarProjectionNoticeKey(
  projection: Pick<DatabaseProjectionResult, 'complete' | 'syncState'>
): DataViewI18nKey | undefined {
  if (projection.complete) {
    return undefined;
  }
  return projection.syncState === 'unsupported'
    ? 'calendar.notice.projectionUnsupported'
    : 'calendar.notice.partialProjection';
}

export class CalendarViewUILogic extends DataViewUILogicBase<
  CalendarSingleView,
  CalendarViewSelectionWithType
> {
  ui$ = signal<CalendarViewUI | undefined>();

  clearSelection = (): void => {
    this.setSelection(undefined);
  };

  addRow = (position: InsertToPosition): string | undefined => {
    if (this.view.readonly$.value) return undefined;
    const rowId = this.view.rowAdd(position);
    this.ui$.value?.requestUpdate();
    this.ui$.value?.queueIntrinsicContentHeightChange();
    return rowId;
  };

  override isSetupWizardOpen(): boolean {
    return this.ui$.value?.isSetupWizardOpen() ?? false;
  }

  focusFirstCell = (): void => {};
  showIndicator = (_evt: MouseEvent): boolean => false;
  hideIndicator = (): void => {};
  moveTo = (_id: string, _evt: MouseEvent): void => {};

  /** Pages to the event's period first; then the event is on screen. */
  override revealRow(rowId: string): boolean {
    return this.ui$.value?.showEventPeriod(rowId) ?? false;
  }

  renderer = createUniComponentFromWebComponent(CalendarViewUI);
}

function calendarMailboxSendEligibilityMessage(
  eligibility: ReturnType<typeof calendarMailboxSendEligibility>
): string {
  if (eligibility.reason === 'not_linked') {
    return dataViewI18nT('calendar.error.mailAccountRequired');
  }
  if (
    eligibility.reason === 'credential_unavailable' ||
    eligibility.reason === 'missing_capability'
  ) {
    return dataViewI18nT('calendar.error.mailboxCredentialRequired');
  }
  return dataViewI18nT('calendar.error.mailboxUnavailable');
}

export class CalendarViewUI extends DataViewUIBase<CalendarViewUILogic> {
  private externalDetailOnly = false;
  private externalDetailHost: HTMLElement | null = null;
  private deleteConfirmationOpen = false;
  private mountMode: CalendarMountMode = 'doc';
  private readonly canvasViewport = new CanvasViewportParticipantController(
    this,
    'calendar',
    () => this.shouldUseSharedCalendarState()
  );
  private readonly allDayCollapsed = signal(false);
  private readonly allDayRenderLimit = signal(
    CALENDAR_ALL_DAY_RENDER_PAGE_SIZE
  );
  private readonly calendarError = signal<string | undefined>();
  private readonly cursor = signal(startOfDay(new Date()));
  private readonly draggedOccurrence = signal<
    CalendarOccurrence<CalendarEvent> | undefined
  >();
  private readonly draggedOccurrenceOffset = signal<
    CalendarDragOffset | undefined
  >();
  private readonly detailShell = new CalendarDetailShellController();
  private detailReturnFocus: HTMLElement | null = null;

  private readonly periodMotion = new CalendarPeriodMotion();
  private readonly dragCreateSlot = signal<CalendarSlotDrag | undefined>();
  private readonly eventEditPreview = signal<
    CalendarEventEditPreview | undefined
  >();
  private readonly icsFeedFormOpen = signal(false);
  private commandSession: CalendarCommandSession | undefined;
  private importSession: CalendarCommandSession | undefined;
  private sourcePopup: { close: () => void } | undefined;
  private readonly pendingCommands = signal<ReadonlySet<CalendarFunctionId>>(
    new Set()
  );
  private icsAbort = new AbortController();
  private readonly icsFeedUrlDraft = signal('');
  private readonly mailboxLinks = signal<readonly CalendarMailboxLink[]>([]);
  private readonly mailboxLinkLoadFailed = signal(false);
  private readonly now = signal(new Date());
  private readonly rangeIndicatorCursor = signal<Date | undefined>();
  private readonly reconcileState = signal<
    Readonly<Record<string, CalendarReconcileState>>
  >({});
  private readonly rangeLoading = signal<CalendarRangeLoading | undefined>();
  private readonly reconcileSnapshots = new Map<
    string,
    CalendarReconcileSnapshot
  >();
  private readonly dirtyEventRows = new Set<string>();
  private readonly selectedOccurrenceStart = signal<string | undefined>();
  private readonly selectedRowId = signal<string | undefined>();
  private readonly subscriptionEvents = signal<
    Readonly<Record<string, CalendarEvent[]>>
  >({});
  private readonly subscriptionStates = signal<
    Readonly<Record<string, CalendarSubscriptionState>>
  >({});
  private readonly timedOffscreen = signal<CalendarTimedOffscreenCounts>({
    bottom: 0,
    top: 0,
  });
  private readonly timedRenderWindow = signal<CalendarTimedRenderWindow>(
    INITIAL_CALENDAR_TIMED_RENDER_WINDOW
  );
  private monthScrollAnchorKey = '';
  private observedCalendarMode: string | undefined;
  private mailboxLinkLoadKey = '';
  private bypassExternalLeavePrompt = false;
  private leavePromptOpen = false;
  private readonly monthYearsAfter = signal(
    initialCalendarSurfaceRange.monthYearsAfter
  );
  private readonly monthYearsBefore = signal(
    initialCalendarSurfaceRange.monthYearsBefore
  );
  private scrollStateCleanup: (() => void) | undefined;
  private scrollStateTarget: HTMLElement | undefined;
  private timeGridScrollCleanup: (() => void) | undefined;
  private timeGridContentOffsetTop = 0;
  private timeGridScrollTarget: HTMLElement | undefined;
  private subscriptionLoadKey = '';
  private readonly weekDaysAfter = signal(
    initialCalendarSurfaceRange.weekDaysAfter
  );
  private readonly weekDaysBefore = signal(
    initialCalendarSurfaceRange.weekDaysBefore
  );
  private currentTimeInterval: ReturnType<typeof setInterval> | undefined;

  /** Controls visibility of the setup-wizard overlay. */
  private readonly wizardOpen = signal(false);

  /** Guards the one-time auto-open of the wizard on first unconfigured mount. */
  private autoOpenSeen = false;

  private readonly databaseRowsProjection$ = computed(() => {
    const view = this.logic.view;
    const { end, start } = this.visibleWindow();
    const startDateCol = view.startDateCol$.value;
    const endDateCol = view.endDateCol$.value;
    const recurrenceCol = view.recurrenceCol$.value;
    const predicates: DatabaseProjectionPredicate[] = [];
    if (startDateCol) {
      const overlap: DatabaseProjectionPredicate = {
        endPropertyId: endDateCol,
        startPropertyId: startDateCol,
        type: 'temporal-overlap',
        windowEnd: end.getTime(),
        windowStart: start.getTime(),
      };
      predicates.push(
        recurrenceCol
          ? {
              operator: 'or',
              predicates: [
                overlap,
                {
                  operator: 'is-not-empty',
                  propertyId: recurrenceCol,
                  type: 'column',
                },
              ],
              type: 'predicate-group',
            }
          : overlap
      );
    }

    return view.projectRows({
      predicates,
      requestedPropertyIds: [
        view.mainProperties$.value.titleColumn,
        startDateCol,
        endDateCol,
        view.allDayCol$.value,
        view.calendarIdCol$.value,
        recurrenceCol,
        view.recurrenceExdateCol$.value,
        view.recurrenceIdCol$.value,
        view.timezoneCol$.value,
      ].filter((propertyId): propertyId is string => Boolean(propertyId)),
      scope: startDateCol ? 'temporal-window' : 'filtered-result',
      viewId: view.id,
      viewMode: view.type,
    });
  });

  private readonly createdRowIds = signal<readonly string[]>([]);

  private readonly allEvents$ = computed(() => {
    const view = this.logic.view;
    const databaseEvents = readCalendarProjectedEvents(
      view,
      this.databaseRowsProjection$.value,
      this.createdRowIds.value
    );
    const subscriptionEvents = Object.values(this.subscriptionEvents.value)
      .flat()
      .filter(event => event.externalReadonly);
    return [...databaseEvents, ...subscriptionEvents].sort((a, b) => {
      if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
      return a.startAt.getTime() - b.startAt.getTime();
    });
  });

  private readonly calendarIds$ = computed(() => {
    const ids = this.allEvents$.value
      .map(event => displayCalendarId(event.calendarId))
      .filter(Boolean);
    return [...new Set(ids)].sort((left, right) => left.localeCompare(right));
  });

  private readonly baseEvents$ = computed(() => {
    const visible = this.logic.view.visibleCalendarIds$.value;
    if (!visible.length) return this.allEvents$.value;
    const allowed = new Set(visible);
    return this.allEvents$.value.filter(event =>
      allowed.has(displayCalendarId(event.calendarId))
    );
  });

  private readonly events$ = computed(() => {
    const { end, start } = this.visibleWindow();
    const recurrenceErrors = this.recurrenceErrors$.value;
    const dragged = this.draggedOccurrence.value;
    const preview = this.eventEditPreview.value;
    return this.baseEvents$.value
      .flatMap(event =>
        expandCalendarOccurrences(
          recurrenceErrors.has(event.rowId)
            ? { ...event, recurrence: '' }
            : event,
          start,
          end
        )
      )
      .map(occurrence =>
        occurrence.source.externalReadonly
          ? occurrence
          : {
              ...occurrence,
              source: hydrateCalendarEventDetails(
                this.logic.view,
                occurrence.source
              ),
            }
      )
      .map(event => this.applyEventEditPreview(event, preview, dragged))
      .sort((a, b) => {
        if (a.source.allDay !== b.source.allDay) {
          return a.source.allDay ? -1 : 1;
        }
        return a.startAt.getTime() - b.startAt.getTime();
      });
  });

  private readonly eventsByDay$ = computed(() => {
    const map = new Map<number, CalendarOccurrence<CalendarEvent>[]>();
    const days = this.visibleDays();
    for (const day of days) {
      map.set(startOfDay(day).getTime(), []);
    }
    const first = days[0];
    const last = days.at(-1);
    if (!first || !last) return map;
    const firstDay = startOfDay(first);
    const windowEnd = endOfDay(last);
    for (const event of this.events$.value) {
      if (
        isAfter(event.startAt, windowEnd) ||
        isBefore(event.endAt, firstDay)
      ) {
        continue;
      }
      const firstIndex = Math.max(
        0,
        differenceInCalendarDays(startOfDay(event.startAt), firstDay)
      );
      const lastIndex = Math.min(
        days.length - 1,
        differenceInCalendarDays(startOfDay(event.endAt), firstDay)
      );
      for (let index = firstIndex; index <= lastIndex; index++) {
        const day = days[index];
        if (!day) continue;
        map.get(startOfDay(day).getTime())?.push(event);
      }
    }
    return map;
  });

  private readonly projectionNoticeKey$ = computed(() =>
    calendarProjectionNoticeKey(this.databaseRowsProjection$.value)
  );

  private readonly recurrenceErrors$ = computed(() => {
    const errors = new Map<string, string>();
    for (const event of this.baseEvents$.value) {
      try {
        validateCalendarRecurrenceRule(event.recurrence);
      } catch (error) {
        errors.set(
          event.rowId,
          error instanceof Error
            ? error.message
            : dataViewI18nT('calendar.grid.invalidRecurrence')
        );
      }
    }
    return errors;
  });

  override connectedCallback(): void {
    super.connectedCallback();
    this.icsAbort = new AbortController();
    this.commandSession = new CalendarCommandSession({
      view: this.logic.view,
      context: this.icsContext(),
      onCreated: async ({ rowId }) => {
        // Retain only a bounded local creation window; the worker remains
        // authoritative for the complete calendar result and its notices.
        const projected = new Set(this.databaseRowsProjection$.peek().rowIds);
        this.createdRowIds.value = [
          ...this.createdRowIds.peek().filter(id => !projected.has(id)),
          rowId,
        ].slice(-100);
        this.setReconcileState(rowId, 'idle');
        this.dirtyEventRows.add(rowId);
        this.requestUpdate();
        this.queueIntrinsicContentHeightChange();
      },
      canEdit: () => !this.logic.view.readonly$.value,
      release: () => {},
    });
    this.logic.ui$.value = this;
    this.mountMode = detectCalendarMountMode(this);
    this.restoreCursorState();
    this.currentTimeInterval = setInterval(() => {
      this.now.value = new Date();
      this.requestUpdate();
    }, 60_000);
    this.classList.add(styles.calendarHost);
    this.classList.add(
      this.mountMode === 'edgeless'
        ? styles.calendarEdgelessMode
        : this.mountMode === 'fullscreen'
          ? styles.calendarFullscreenMode
          : styles.calendarDocMode
    );
    this.dataset.calendarMode = this.mountMode;
    this.style.display = 'flex';
    this.style.flexDirection = 'column';

    this.externalDetailHost = this.closest('database-fullscreen-editor');
    this.externalDetailHost?.addEventListener(
      CALENDAR_DETAIL_TARGET_EVENT,
      this.onExternalDetailTarget
    );
    this.externalDetailHost?.addEventListener(
      CALENDAR_DETAIL_DELETE_EVENT,
      this.onExternalDetailDelete
    );
    this.syncExternalDetailTarget();

    const onWizardLayoutChange = () => this.queueIntrinsicContentHeightChange();
    this.addEventListener(
      CALENDAR_SETUP_WIZARD_LAYOUT_CHANGE_EVENT,
      onWizardLayoutChange
    );
    this.disposables.add(() => {
      this.removeEventListener(
        CALENDAR_SETUP_WIZARD_LAYOUT_CHANGE_EVENT,
        onWizardLayoutChange
      );
    });

    // Auto-open the setup wizard once when the calendar is not fully wired.
    // A new calendar view can inherit start/end date columns from another
    // view, so `isConfigured$` alone is not enough to prove setup completed.
    this.disposables.add(
      effect(() => {
        const view = this.logic.view;
        if (
          (!this.isConfigured() ||
            view.optionalMissingColumnRoles$.value.length > 0) &&
          !this.autoOpenSeen &&
          !view.readonly$.value
        ) {
          this.autoOpenSeen = true;
          this.setWizardOpen(true);
        }
      })
    );
    this.disposables.add(
      effect(() => {
        if (!this.shouldUseSharedCalendarState()) return;
        const sharedTime = this.logic.view.calendarCursorTime$.value;
        if (!Number.isFinite(sharedTime)) return;
        const sharedCursor = startOfDay(new Date(sharedTime as number));
        if (!isSameDay(sharedCursor, this.cursor.value)) {
          this.resetAllDayRenderLimit();
          this.cursor.value = sharedCursor;
        }
      })
    );
    this.disposables.add(
      effect(() => {
        const mode = this.logic.view.calendarMode$.value;
        if (
          this.observedCalendarMode !== undefined &&
          this.observedCalendarMode !== mode
        ) {
          this.resetAllDayRenderLimit();
        }
        this.observedCalendarMode = mode;
      })
    );
    this.disposables.add(
      effect(() => {
        const context = resolveCalendarReconcileContext(
          this.logic.view.manager.dataSource
        );
        // Do not mark an actor-scoped load complete before identity is ready.
        // The user signal will rerun this effect when the account is available.
        if (!context?.actorUserId) return;
        const key = `${context.actorUserId}:${context.workspaceId}:${context.docId}:${context.databaseId}:${this.logic.view.id}`;
        if (key === this.mailboxLinkLoadKey) return;
        this.mailboxLinkLoadKey = key;
        void this.loadMailboxLinks(context);
      })
    );
    this.disposables.add(
      subscribeCalendarBackendAvailable(
        this.logic.view.manager.dataSource,
        () => {
          const context = resolveCalendarReconcileContext(
            this.logic.view.manager.dataSource
          );
          if (context) void this.loadMailboxLinks(context);
        }
      )
    );
    document.addEventListener('click', this.onDocumentClickCapture, true);
    this.disposables.add(() => {
      document.removeEventListener('click', this.onDocumentClickCapture, true);
    });
  }

  private readonly openWizard = (): void => {
    this.setWizardOpen(true);
  };

  private readonly closeWizard = (): void => {
    this.setWizardOpen(false);
  };

  private getBodyState(): DataViewBodyState {
    if (this.wizardOpen.value) {
      return 'setup';
    }
    return this.isConfigured() ? 'data' : 'empty';
  }

  private isConfigured(): boolean {
    const view = this.logic.view;
    return (
      view.isConfigured$.value &&
      view.requiredMissingColumnRoles$.value.length === 0
    );
  }

  private async loadMailboxLinks(
    context: ReturnType<typeof resolveCalendarReconcileContext>
  ): Promise<void> {
    const source = this.logic.view.manager.dataSource as CalendarDataSourceLike;
    // Existing account cookies/identity do not change the owning guest view.
    if (
      !context?.actorUserId ||
      !source.doc?.workspace ||
      source.doc.workspace.externalDocAccess
    )
      return;
    const signal = this.icsAbort.signal;
    try {
      // The view mounts from local state before its root/view definition is
      // necessarily available to the server's mailbox-policy validator.
      await flushCalendarReconcileDocs(context, signal);
      signal.throwIfAborted();
      const links = await loadCalendarMailboxLinks(source.doc.workspace, {
        ...context,
        actorUserId: context.actorUserId,
        viewId: this.logic.view.id,
      });
      if (signal.aborted) return;
      this.mailboxLinks.value = links.links;
      this.mailboxLinkLoadFailed.value = false;
    } catch (error) {
      if (signal.aborted) return;
      this.mailboxLinks.value = [];
      this.mailboxLinkLoadFailed.value = true;
      console.warn(
        '[calendar] Mailbox links could not be loaded; calendar editing remains available.',
        error
      );
    }
    this.requestUpdate();
  }

  override disconnectedCallback(): void {
    this.icsAbort.abort();
    this.commandSession?.dispose();
    this.commandSession = undefined;
    this.importSession = undefined;
    this.sourcePopup?.close();
    this.sourcePopup = undefined;
    this.pendingCommands.value = new Set();
    this.createdRowIds.value = [];
    this.externalDetailHost?.removeEventListener(
      CALENDAR_DETAIL_TARGET_EVENT,
      this.onExternalDetailTarget
    );
    this.externalDetailHost?.removeEventListener(
      CALENDAR_DETAIL_DELETE_EVENT,
      this.onExternalDetailDelete
    );
    this.externalDetailHost = null;
    if (this.logic.ui$.value === this) this.logic.ui$.value = undefined;
    if (this.currentTimeInterval !== undefined) {
      clearInterval(this.currentTimeInterval);
      this.currentTimeInterval = undefined;
    }
    this.scrollStateCleanup?.();
    this.scrollStateCleanup = undefined;
    this.scrollStateTarget = undefined;
    this.timeGridScrollCleanup?.();
    this.timeGridScrollCleanup = undefined;
    this.timeGridScrollTarget = undefined;
    this.canvasViewport.dispose();
    this.detailShell.dispose();
    this.periodMotion.dispose();
    super.disconnectedCallback();
  }

  private readonly onExternalDetailDelete = (event: Event): void => {
    const detail = (event as CustomEvent<CalendarDetailDeleteRequest>).detail;
    if (detail.viewId !== this.logic.view.id) return;
    detail.request = this.requestDeleteEvent(detail.rowId);
  };

  private readonly onExternalDetailTarget = (event: Event): void => {
    this.applyExternalDetailTarget(
      (event as CustomEvent<CalendarDetailTarget>).detail
    );
  };

  private syncExternalDetailTarget(): void {
    const host = this.externalDetailHost as
      | (HTMLElement & {
          calendarDetailOnly?: boolean;
          calendarDetailOccurrenceStart?: string | null;
          calendarDetailRowId?: string | null;
          calendarDetailViewId?: string | null;
        })
      | null;
    if (!host) return;
    this.applyExternalDetailTarget({
      detailOnly: host.calendarDetailOnly === true,
      occurrenceStart: host.calendarDetailOccurrenceStart ?? null,
      rowId: host.calendarDetailRowId ?? null,
      viewId: host.calendarDetailViewId ?? null,
    });
  }

  private applyExternalDetailTarget(target: CalendarDetailTarget): void {
    if (target.viewId !== this.logic.view.id) return;
    this.externalDetailOnly = target.detailOnly;
    const apply = () => {
      this.selectedRowId.value = target.rowId ?? undefined;
      this.selectedOccurrenceStart.value = target.occurrenceStart ?? undefined;
      if (target.rowId) {
        this.logic.setSelection({
          rowId: target.rowId,
          selectionType: 'event',
          type: 'calendar',
          viewId: this.logic.view.id,
        });
      }
      this.requestUpdate();
    };
    if (
      this.selectedRowId.value &&
      target.rowId &&
      this.selectedRowId.value !== target.rowId
    ) {
      void this.leaveSelectedEvent(apply);
      return;
    }
    apply();
  }

  private dispatchExternalDetailClose(): void {
    window.dispatchEvent(new Event(CALENDAR_DETAIL_CLOSE_EVENT));
  }

  protected override updated(): void {
    this.syncCalendarScrollAnchors();
    this.syncCalendarScrollState();
    this.syncTimeGridScrollState();
    this.syncCanvasViewportParticipants();
    this.syncDataViewBodyResizeObserver({
      body: this.getRootElement(),
    });
    const detailRoot = this.getRootElement()?.querySelector<HTMLElement>(
      '[data-calendar-detail-shell-mount]'
    );
    if (detailRoot && !this.externalDetailOnly) {
      this.detailShell.update(detailRoot, {
        contentKey: this.selectedRowId.value
          ? `${this.logic.view.id}:${this.selectedRowId.value}:${this.selectedOccurrenceStart.value}`
          : null,
        content: this.renderDetails(),
        width: CALENDAR_DETAIL_DEFAULT_WIDTH,
        overlay: false,
        returnFocus: this.detailReturnFocus,
      });
    }
    this.settlePeriodMotion();
    this.queueIntrinsicContentHeightChange();
  }

  private settlePeriodMotion(): void {
    this.periodMotion.settle(
      this.getRootElement(),
      this.logic.view.calendarMode$.value,
      this.cursor.value
    );
  }

  override getIntrinsicContentHeight(visualScale = 1): number {
    const scale = visualScale || 1;
    const root = this.getRootElement();
    const wizardHeight = this.getWizardIntrinsicHeight(scale);
    const bodyHeight = measureDataViewBodyIntrinsicHeight({
      body: root,
      visualScale: scale,
    });

    const edgelessGuard =
      this.mountMode === 'edgeless'
        ? CALENDAR_EDGELESS_INTRINSIC_HEIGHT_GUARD
        : 0;

    if (this.wizardOpen.value) {
      return Math.max(bodyHeight, wizardHeight) + edgelessGuard;
    }

    if (this.isConfigured()) {
      const configuredContentHeight =
        bodyHeight > 0
          ? bodyHeight
          : this.getConfiguredContentIntrinsicHeight(scale);
      return Math.max(configuredContentHeight, wizardHeight) + edgelessGuard;
    }

    return (
      Math.max(
        CALENDAR_EMPTY_STATE_INTRINSIC_HEIGHT,
        bodyHeight,
        wizardHeight
      ) + edgelessGuard
    );
  }

  isSetupWizardOpen(): boolean {
    return this.wizardOpen.value;
  }

  private dispatchSetupWizardStateChange(): void {
    dispatchDatabaseLiveViewSetupStateChange(this, this.wizardOpen.value);
  }

  private setWizardOpen(open: boolean): void {
    if (this.wizardOpen.value === open) {
      return;
    }
    this.wizardOpen.value = open;
    this.dispatchSetupWizardStateChange();
    this.queueIntrinsicContentHeightChange();
    this.requestUpdate();
  }

  private getConfiguredContentIntrinsicHeight(visualScale: number): number {
    const calendarHeight = this.getCalendarGridIntrinsicHeight();
    const details = this.getRootElement()?.querySelector<HTMLElement>(
      `.${styles.details}`
    );
    const detailsHeight = details
      ? Math.max(
          details.scrollHeight,
          details.getBoundingClientRect().height / visualScale
        )
      : 0;
    return Math.max(calendarHeight, detailsHeight);
  }

  private getCalendarGridIntrinsicHeight(): number {
    const mode = this.logic.view.calendarMode$.value;
    if (mode === 'month') {
      return 6 * CALENDAR_MONTH_CELL_HEIGHT;
    }
    const timeGridHeight =
      (this.allDayCollapsed.value
        ? CALENDAR_ALL_DAY_BAND_COLLAPSED_HEIGHT
        : CALENDAR_ALL_DAY_BAND_HEIGHT) +
      24 * CALENDAR_SURFACE_HOUR_HEIGHT;
    return mode === 'week'
      ? CALENDAR_WEEK_HEADER_HEIGHT + timeGridHeight
      : timeGridHeight;
  }

  private getRootElement(): HTMLElement | undefined {
    return getDataViewBodyElement(this);
  }

  private calendarLocalStateKey(suffix: string): string {
    return `calendar.${suffix}:${this.logic.view.id}`;
  }

  private shouldUseSharedCalendarState(): boolean {
    return (
      this.mountMode === 'edgeless' ||
      this.logic.view.featureFlags$.value.use_shared_canvas_viewport
    );
  }

  applyCanvasViewportTarget(
    targetId: string,
    position: CanvasViewportTargetPosition
  ): boolean {
    return this.canvasViewport.applyTarget(targetId, position);
  }

  getCanvasViewportTargets(): Record<string, CanvasViewportTargetPosition> {
    return this.canvasViewport.getTargets();
  }

  private calendarDateScrollTargetId(
    target: HTMLElement | undefined
  ): string | undefined {
    if (!target) return undefined;
    if (target.hasAttribute('data-calendar-week-scroll')) {
      return CALENDAR_CANVAS_WEEK_SCROLL_TARGET;
    }
    if (target.hasAttribute('data-calendar-month-scroll')) {
      return CALENDAR_CANVAS_MONTH_SCROLL_TARGET;
    }
    return undefined;
  }

  private syncCanvasViewportParticipants(): void {
    const dateScrollTargetId = this.calendarDateScrollTargetId(
      this.scrollStateTarget
    );
    this.canvasViewport.sync([
      dateScrollTargetId
        ? createElementCanvasViewportTarget(
            dateScrollTargetId,
            this.scrollStateTarget,
            'both'
          )
        : undefined,
      createElementCanvasViewportTarget(
        CALENDAR_CANVAS_TIME_SCROLL_TARGET,
        this.timeGridScrollTarget,
        'y'
      ),
    ]);
  }

  private restoreCursorState(): void {
    const sharedTime = this.logic.view.calendarCursorTime$.value;
    if (this.shouldUseSharedCalendarState() && Number.isFinite(sharedTime)) {
      this.cursor.value = startOfDay(new Date(sharedTime as number));
      return;
    }
    if (this.shouldUseSharedCalendarState()) return;
    if (typeof localStorage === 'undefined') return;
    try {
      const cursorTime = parseCalendarCursorTime(
        localStorage.getItem(this.calendarLocalStateKey('cursor'))
      );
      if (cursorTime !== undefined) {
        this.cursor.value = startOfDay(new Date(cursorTime));
      }
    } catch {
      // Private browsing / quota: start at today.
    }
  }

  /**
   * Pages to the period of a row's event, as a person would. The stored and the
   * shared period stay: only this window looks there.
   */
  showEventPeriod(rowId: string): boolean {
    const event = readCalendarEvent(this.logic.view, rowId);
    if (!event) return false;
    this.setCalendarCursor(event.startAt, { persist: false });
    return true;
  }

  private setCalendarCursor(
    date: Date,
    options: { anchorScroll?: boolean; persist?: boolean } = {}
  ): void {
    const anchorScroll = options.anchorScroll ?? true;
    const persist = options.persist ?? true;
    const next = startOfDay(date);
    this.resetAllDayRenderLimit();
    this.rangeIndicatorCursor.value = undefined;
    if (!isSameDay(next, this.cursor.value)) {
      this.cursor.value = next;
    }
    if (!anchorScroll) {
      this.monthScrollAnchorKey = calendarSurfaceMonthAnchorKey(next);
    }
    if (!persist) return;
    if (this.shouldUseSharedCalendarState()) {
      this.logic.view.setCalendarCursorTime(next.getTime());
      return;
    }
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(
        this.calendarLocalStateKey('cursor'),
        String(next.getTime())
      );
    } catch {
      // Ignore quota.
    }
  }

  private syncCalendarScrollAnchors(): void {
    if (this.logic.view.calendarMode$.value !== 'month') return;
    const cursor = this.cursor.value;
    const key = calendarSurfaceMonthAnchorKey(cursor);
    if (this.monthScrollAnchorKey === key) return;
    this.monthScrollAnchorKey = key;
    requestAnimationFrame(() => {
      const root = this.getRootElement();
      const scroll = root?.querySelector<HTMLElement>(
        '[data-calendar-month-scroll]'
      );
      const anchor = root?.querySelector<HTMLElement>(
        `[data-calendar-month-anchor="${key}"]`
      );
      if (!scroll || !anchor) return;
      scroll.scrollTop = Math.max(0, anchor.offsetTop - scroll.offsetTop);
    });
  }

  private syncCalendarScrollState(): void {
    const root = this.getRootElement();
    const target = root?.querySelector<HTMLElement>(
      '[data-calendar-week-scroll], [data-calendar-month-scroll]'
    );
    if (target === this.scrollStateTarget) return;
    this.scrollStateCleanup?.();
    this.scrollStateCleanup = undefined;
    this.scrollStateTarget = target ?? undefined;
    if (!target) return;

    const kind = target.hasAttribute('data-calendar-week-scroll')
      ? 'week'
      : 'month';
    const persistLocally = !this.shouldUseSharedCalendarState();
    if (persistLocally) {
      requestAnimationFrame(() => {
        if (this.scrollStateTarget !== target) return;
        if (typeof localStorage === 'undefined') return;
        try {
          const leftValue = localStorage.getItem(
            this.calendarLocalStateKey(`${kind}.scrollLeft`)
          );
          const topValue = localStorage.getItem(
            this.calendarLocalStateKey(`${kind}.scrollTop`)
          );
          const left = leftValue === null ? 0 : Number(leftValue);
          const top =
            topValue === null && kind === 'week'
              ? DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE *
                CALENDAR_TIMED_EVENT_PX_PER_MINUTE
              : Number(topValue);
          if (Number.isFinite(left) && left >= 0) target.scrollLeft = left;
          if (Number.isFinite(top) && top >= 0) target.scrollTop = top;
        } catch {
          if (kind === 'week') {
            target.scrollTop =
              DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE *
              CALENDAR_TIMED_EVENT_PX_PER_MINUTE;
          }
        }
      });
    } else if (kind === 'week') {
      requestAnimationFrame(() => {
        if (this.scrollStateTarget !== target) return;
        target.scrollTop =
          DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE *
          CALENDAR_TIMED_EVENT_PX_PER_MINUTE;
      });
    }

    let persistTimer: ReturnType<typeof setTimeout> | undefined;
    const onScroll = (): void => {
      if (kind === 'month') this.updateMonthCursorFromScroll(target);
      else {
        this.updateWeekCursorFromScroll(target);
        this.maybeLoadCalendarRange(target, 'week');
      }
      if (!persistLocally) return;
      if (persistTimer !== undefined) clearTimeout(persistTimer);
      persistTimer = setTimeout(() => {
        persistTimer = undefined;
        if (typeof localStorage === 'undefined') return;
        try {
          localStorage.setItem(
            this.calendarLocalStateKey(`${kind}.scrollLeft`),
            String(target.scrollLeft)
          );
          localStorage.setItem(
            this.calendarLocalStateKey(`${kind}.scrollTop`),
            String(target.scrollTop)
          );
        } catch {
          // Ignore quota.
        }
      }, 500);
    };
    target.addEventListener('scroll', onScroll, { passive: true });
    this.scrollStateCleanup = () => {
      if (persistTimer !== undefined) clearTimeout(persistTimer);
      target.removeEventListener('scroll', onScroll);
    };
  }

  private syncTimeGridScrollState(): void {
    const root = this.getRootElement();
    const timeGrid = root?.querySelector<HTMLElement>(
      '[data-calendar-time-scroll]'
    );
    const target =
      this.logic.view.calendarMode$.value === 'week'
        ? root?.querySelector<HTMLElement>('[data-calendar-week-scroll]')
        : timeGrid;
    if (target === this.timeGridScrollTarget) {
      requestAnimationFrame(() => {
        if (this.timeGridScrollTarget === target && target) {
          this.timeGridContentOffsetTop =
            timeGrid && timeGrid !== target
              ? calendarElementOffsetWithinAncestor(timeGrid, target)
              : 0;
          this.updateTimedOffscreenCounts(target);
        }
      });
      return;
    }
    this.timeGridScrollCleanup?.();
    this.timeGridScrollCleanup = undefined;
    this.timeGridScrollTarget = target ?? undefined;
    if (!target) {
      this.timeGridContentOffsetTop = 0;
      this.setTimedOffscreenCounts({ bottom: 0, top: 0 });
      this.setTimedRenderWindow(INITIAL_CALENDAR_TIMED_RENDER_WINDOW);
      return;
    }

    const onScroll = (): void => this.updateTimedOffscreenCounts(target);
    target.addEventListener('scroll', onScroll, { passive: true });
    const resizeObserver = new ResizeObserver(() => {
      this.timeGridContentOffsetTop =
        timeGrid && timeGrid !== target
          ? calendarElementOffsetWithinAncestor(timeGrid, target)
          : 0;
      this.updateTimedOffscreenCounts(target);
    });
    resizeObserver.observe(target);
    if (timeGrid && timeGrid !== target) resizeObserver.observe(timeGrid);
    requestAnimationFrame(() => {
      if (this.timeGridScrollTarget === target) {
        this.timeGridContentOffsetTop =
          timeGrid && timeGrid !== target
            ? calendarElementOffsetWithinAncestor(timeGrid, target)
            : 0;
        this.updateTimedOffscreenCounts(target);
      }
    });
    this.timeGridScrollCleanup = () => {
      streamLainAnimationController.cancel(target);
      resizeObserver.disconnect();
      target.removeEventListener('scroll', onScroll);
    };
  }

  private setTimedOffscreenCounts(next: CalendarTimedOffscreenCounts): void {
    const current = this.timedOffscreen.value;
    if (current.top === next.top && current.bottom === next.bottom) return;
    this.timedOffscreen.value = next;
  }

  private resetAllDayRenderLimit(): void {
    if (this.allDayRenderLimit.value !== CALENDAR_ALL_DAY_RENDER_PAGE_SIZE) {
      this.allDayRenderLimit.value = CALENDAR_ALL_DAY_RENDER_PAGE_SIZE;
    }
  }

  private setTimedRenderWindow(next: CalendarTimedRenderWindow): void {
    const current = this.timedRenderWindow.value;
    if (
      current.startMinute === next.startMinute &&
      current.endMinute === next.endMinute
    ) {
      return;
    }
    this.timedRenderWindow.value = next;
  }

  private timedOccurrencePositions(): {
    endMinute: number;
    key: string;
    startMinute: number;
  }[] {
    if (this.logic.view.calendarMode$.value === 'month') return [];
    const positions: {
      endMinute: number;
      key: string;
      startMinute: number;
    }[] = [];
    for (const day of this.visibleDays()) {
      for (const event of this.eventsForDay(day)) {
        if (event.source.allDay) continue;
        const { heightMinute, startMinute } = timedEventGeometry(
          day,
          event.startAt,
          event.endAt
        );
        positions.push({
          endMinute: startMinute + heightMinute,
          key: `${event.source.rowId}:${event.originalStartAt.toISOString()}`,
          startMinute,
        });
      }
    }
    return positions;
  }

  private updateTimedOffscreenCounts(target: HTMLElement): void {
    if (target.clientHeight <= 0) {
      this.setTimedOffscreenCounts({ bottom: 0, top: 0 });
      return;
    }
    const { renderWindow, visibleBottom, visibleTop } = calendarTimedViewport(
      target.scrollTop,
      target.clientHeight,
      this.timeGridContentOffsetTop,
      CALENDAR_TIMED_RENDER_OVERSCAN_MINUTES
    );
    this.setTimedRenderWindow(renderWindow);
    const top = new Set<string>();
    const bottom = new Set<string>();
    for (const event of this.timedOccurrencePositions()) {
      if (event.endMinute <= visibleTop) top.add(event.key);
      else if (event.startMinute >= visibleBottom) bottom.add(event.key);
    }
    this.setTimedOffscreenCounts({
      bottom: bottom.size,
      top: top.size,
    });
  }

  private readonly jumpToHiddenTimedEvents = (side: 'bottom' | 'top'): void => {
    const target = this.timeGridScrollTarget;
    if (!target) return;
    const { visibleBottom, visibleTop } = calendarTimedViewport(
      target.scrollTop,
      target.clientHeight,
      this.timeGridContentOffsetTop,
      0
    );
    let targetMinute: number | undefined;
    for (const event of this.timedOccurrencePositions()) {
      if (side === 'top') {
        if (event.endMinute > visibleTop) continue;
        if (targetMinute === undefined || event.startMinute > targetMinute) {
          targetMinute = event.startMinute;
        }
      } else {
        if (event.startMinute < visibleBottom) continue;
        if (targetMinute === undefined || event.startMinute < targetMinute) {
          targetMinute = event.startMinute;
        }
      }
    }
    if (targetMinute === undefined) return;
    streamLainAnimationController.scrollTo(target, {
      top: Math.max(
        0,
        this.timeGridContentOffsetTop +
          targetMinute * CALENDAR_TIMED_EVENT_PX_PER_MINUTE -
          24
      ),
    });
  };

  private updateMonthCursorFromScroll(target: HTMLElement): void {
    this.maybeLoadCalendarRange(target, 'month');
    const next = calendarSurfaceScrollCursor(
      target,
      'month',
      this.visibleDays()
    );
    if (!next) return;
    const current = this.rangeIndicatorCursor.value ?? this.cursor.value;
    if (isSameMonth(next, current)) return;
    this.rangeIndicatorCursor.value = next;
  }

  private updateWeekCursorFromScroll(target: HTMLElement): void {
    const weekStart = calendarSurfaceScrollCursor(
      target,
      'week',
      this.visibleDays()
    );
    if (!weekStart) return;
    const currentWeekStart = startOfWeek(
      this.rangeIndicatorCursor.value ?? this.cursor.value,
      calendarWeekStartsOn
    );
    if (isSameDay(weekStart, currentWeekStart)) return;
    this.rangeIndicatorCursor.value = weekStart;
  }

  private maybeLoadCalendarRange(
    target: HTMLElement,
    mode: 'month' | 'week'
  ): void {
    if (this.rangeLoading.value) return;
    const session = beginCalendarSurfaceRangeLoad(target, mode, {
      monthYearsAfter: this.monthYearsAfter.value,
      monthYearsBefore: this.monthYearsBefore.value,
      weekDaysAfter: this.weekDaysAfter.value,
      weekDaysBefore: this.weekDaysBefore.value,
    });
    if (!session) {
      this.calendarError.value = dataViewI18nT('calendar.chrome.unavailable');
      return;
    }
    this.rangeLoading.value = session.loading;
    this.requestUpdate();
    requestAnimationFrame(() => {
      this.monthYearsAfter.value = session.nextRange.monthYearsAfter;
      this.monthYearsBefore.value = session.nextRange.monthYearsBefore;
      this.weekDaysAfter.value = session.nextRange.weekDaysAfter;
      this.weekDaysBefore.value = session.nextRange.weekDaysBefore;
      this.requestUpdate();
      requestAnimationFrame(() => {
        restoreCalendarSurfaceRangeScroll(session);
        this.rangeLoading.value = undefined;
        this.requestUpdate();
      });
    });
  }

  private getWizardIntrinsicHeight(visualScale: number): number {
    return getSetupWizardBodyIntrinsicHeight({
      host: this.querySelector<HTMLElement>('dv-calendar-setup-wizard'),
      visualScale,
    });
  }

  private setCellJson(
    rowId: string,
    propertyId: string | undefined,
    value: unknown
  ): void {
    if (this.logic.view.readonly$.value) return;
    writeCalendarCellValue(this.logic.view, rowId, propertyId, value);
    this.setReconcileState(rowId, 'idle');
    this.dirtyEventRows.add(rowId);
    this.requestUpdate();
    this.queueIntrinsicContentHeightChange();
  }

  private renameOccurrence(
    event: CalendarOccurrence<CalendarEvent>,
    title: string
  ): void {
    if (event.source.externalReadonly) return;
    const nextTitle = title.trim() || dataViewI18nT('calendar.event.untitled');
    if (nextTitle === event.source.title) return;
    this.setCellJson(
      event.source.rowId,
      this.logic.view.mainProperties$.value.titleColumn,
      nextTitle
    );
  }

  private createEvent(day = this.cursor.value, hour = 9): void {
    this.createEventRange(
      day,
      hour * 60,
      hour * 60 + CALENDAR_NEW_EVENT_MINUTES
    );
  }

  private createEventRange(
    day: Date,
    startMinute: number,
    endMinute: number,
    options: { allDay?: boolean } = {}
  ): void {
    void this.runSourceCommand(async session => {
      const { rowId } = await session.createEvent({
        day,
        startMinute,
        endMinute,
        allDay: options.allDay,
      });
      const created = readCalendarEvent(session.view, rowId, {
        includeDetails: true,
      });
      if (!created)
        throw new CalendarCommandError(
          'unavailable',
          'calendar.chrome.unavailable'
        );
      if (session !== this.commandSession || session.view !== this.logic.view)
        return;
      this.selectedRowId.value = rowId;
      this.selectedOccurrenceStart.value = created.startAt.toISOString();
      this.logic.setSelection({
        rowId,
        selectionType: 'event',
        type: 'calendar',
        viewId: session.view.id,
      });
    }, 'create');
  }

  private applyEventEditPreview(
    event: CalendarOccurrence<CalendarEvent>,
    preview: CalendarEventEditPreview | undefined,
    dragged: CalendarOccurrence<CalendarEvent> | undefined
  ): CalendarOccurrence<CalendarEvent> {
    if (
      !preview ||
      event.source.rowId !== preview.rowId ||
      event.startAt.toISOString() !== preview.occurrenceStart
    ) {
      return event;
    }
    if (
      dragged &&
      dragged.source.rowId === event.source.rowId &&
      dragged.originalStartAt.getTime() === event.originalStartAt.getTime()
    ) {
      return event;
    }
    const source = {
      ...event.source,
      allDay: preview.allDay ?? false,
      endAt: preview.endAt,
      startAt: preview.startAt,
    };
    return {
      ...event,
      endAt: preview.endAt,
      source,
      startAt: preview.startAt,
    };
  }

  private readonly clearCalendarEventEditPreview = (): void => {
    this.eventEditPreview.value = undefined;
  };

  private readonly commitCalendarEventEdit = (
    event: CalendarOccurrence<CalendarEvent>,
    startAt: Date,
    endAt: Date,
    options: { allDay?: boolean } = {}
  ): void => {
    this.eventEditPreview.value = undefined;
    const source = event.source;
    this.setCellJson(
      source.rowId,
      this.logic.view.startDateCol$.value,
      startAt.getTime()
    );
    this.setCellJson(
      source.rowId,
      this.logic.view.endDateCol$.value,
      endAt.getTime()
    );
    if (options.allDay !== undefined) {
      this.setCellJson(
        source.rowId,
        this.logic.view.allDayCol$.value,
        options.allDay
      );
    } else if (source.allDay) {
      this.setCellJson(source.rowId, this.logic.view.allDayCol$.value, false);
    }
    if (this.selectedRowId.value === source.rowId) {
      this.selectedOccurrenceStart.value = startAt.toISOString();
    }
  };

  private readonly previewCalendarEventEdit = (
    event: CalendarOccurrence<CalendarEvent>,
    startAt: Date,
    endAt: Date,
    options: { allDay?: boolean } = {}
  ): void => {
    if (this.logic.view.readonly$.value || event.source.externalReadonly) {
      return;
    }
    this.eventEditPreview.value = {
      allDay: options.allDay,
      endAt,
      occurrenceStart: event.startAt.toISOString(),
      rowId: event.source.rowId,
      startAt,
    };
  };

  private readonly previewCalendarOccurrenceMove = (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date,
    startMinute: number
  ): void => {
    const next = this.resolveCalendarOccurrenceMove(event, day, startMinute);
    this.previewCalendarEventEdit(event, next.startAt, next.endAt, {
      allDay: false,
    });
  };

  private readonly previewCalendarOccurrenceMoveToDay = (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ): void => {
    const next = this.resolveCalendarOccurrenceDayMove(event, day);
    this.previewCalendarEventEdit(event, next.startAt, next.endAt, {
      allDay: event.source.allDay,
    });
  };

  private readonly previewCalendarOccurrenceMoveToAllDay = (
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ): void => {
    const next = this.resolveCalendarOccurrenceAllDayMove(event, day);
    this.previewCalendarEventEdit(event, next.startAt, next.endAt, {
      allDay: true,
    });
  };

  private resolveCalendarOccurrenceMove(
    event: CalendarOccurrence<CalendarEvent>,
    day: Date,
    startMinute: number
  ): { endAt: Date; startAt: Date } {
    const durationMinutes = Math.max(
      CALENDAR_TIMED_EVENT_MINUTES,
      Math.round((event.endAt.getTime() - event.startAt.getTime()) / 60_000)
    );
    const startAt = dateAtDayMinute(
      day,
      snapCalendarTimedStartMinute(startMinute)
    );
    return {
      endAt: addMinutes(startAt, durationMinutes),
      startAt,
    };
  }

  private resolveCalendarOccurrenceDayMove(
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ): { endAt: Date; startAt: Date } {
    const delta = differenceInCalendarDays(day, event.startAt);
    return {
      endAt: addDays(event.endAt, delta),
      startAt: addDays(event.startAt, delta),
    };
  }

  private resolveCalendarOccurrenceAllDayMove(
    event: CalendarOccurrence<CalendarEvent>,
    day: Date
  ): { endAt: Date; startAt: Date } {
    const daySpan = Math.max(
      1,
      differenceInCalendarDays(
        startOfDay(event.endAt),
        startOfDay(event.startAt)
      ) + 1
    );
    const startAt = startOfDay(day);
    return {
      endAt: endOfDay(addDays(startAt, daySpan - 1)),
      startAt,
    };
  }

  private finishSlotDrag(day: Date, endMinute: number): void {
    const slot = this.dragCreateSlot.value;
    this.dragCreateSlot.value = undefined;
    if (!slot) return;
    if (slot.allDay) {
      const firstDay = startOfDay(
        slot.day.getTime() <= day.getTime() ? slot.day : day
      );
      const lastDay = startOfDay(
        slot.day.getTime() <= day.getTime() ? day : slot.day
      );
      const dayCount = differenceInCalendarDays(lastDay, firstDay) + 1;
      this.createEventRange(firstDay, 0, dayCount * 24 * 60 - 1, {
        allDay: true,
      });
      return;
    }
    const origin = startOfDay(slot.day).getTime() + slot.startMinute * 60_000;
    const endpoint = startOfDay(day).getTime() + endMinute * 60_000;
    const start = Math.min(origin, endpoint);
    const end =
      Math.max(origin, endpoint) + CALENDAR_TIMED_EVENT_MINUTES * 60_000;
    const rangeDay = startOfDay(new Date(start));
    this.createEventRange(
      rangeDay,
      Math.round((start - rangeDay.getTime()) / 60_000),
      Math.round((end - rangeDay.getTime()) / 60_000),
      { allDay: slot.allDay }
    );
  }

  private shiftCursor(delta: -1 | 1): void {
    const mode = this.logic.view.calendarMode$.value;
    const cursor = this.rangeIndicatorCursor.value ?? this.cursor.value;
    const next =
      mode === 'day'
        ? addDays(cursor, delta)
        : mode === 'week'
          ? addWeeks(cursor, delta)
          : addMonths(cursor, delta);
    this.setCalendarCursor(next);
  }

  private visibleDays(): Date[] {
    return calendarSurfaceDays(
      this.cursor.value,
      this.logic.view.calendarMode$.value,
      {
        monthYearsAfter: this.monthYearsAfter.value,
        monthYearsBefore: this.monthYearsBefore.value,
        weekDaysAfter: this.weekDaysAfter.value,
        weekDaysBefore: this.weekDaysBefore.value,
      }
    );
  }

  private visibleWindow(): { end: Date; start: Date } {
    const days = this.visibleDays();
    const first = days[0] ?? this.cursor.value;
    const last = days[days.length - 1] ?? first;
    return {
      end: endOfDay(last),
      start: startOfDay(first),
    };
  }

  private eventsForDay(day: Date): CalendarOccurrence<CalendarEvent>[] {
    return this.eventsByDay$.value.get(startOfDay(day).getTime()) ?? [];
  }

  private allDayEventsForDays(
    days: readonly Date[]
  ): CalendarOccurrence<CalendarEvent>[] {
    const first = days[0];
    const last = days.at(-1);
    if (!first || !last) return [];
    const windowStart = startOfDay(first);
    const windowEnd = endOfDay(last);
    return this.events$.value.filter(event => {
      if (!event.source.allDay) return false;
      return !(
        isAfter(event.startAt, windowEnd) || isBefore(event.endAt, windowStart)
      );
    });
  }

  private renderToolbar(): TemplateResult {
    const view = this.logic.view;
    const sourceAvailable = Boolean(this.commandSession);
    const network =
      typeof navigator === 'undefined' || navigator.onLine !== false;
    const readonly = view.readonly$.value;
    return html`${renderCalendarChrome({
        state: {
          host:
            this.mountMode === 'edgeless'
              ? 'canvas'
              : this.mountMode === 'fullscreen'
                ? 'fullscreen'
                : 'page',
          canConfigureView: !readonly,
          mode: view.calendarMode$.value,
          cursor: this.rangeIndicatorCursor.value ?? this.cursor.value,
          display: view.displaySettings$.value,
          selectedSourceId: view.id,
          sourceIdentityCount: this.calendarIds$.value.length,
          functions: CALENDAR_FUNCTION_IDS.map(id => {
            const writes = ![
              'export-ics',
              'refresh-feed',
              'mailbox-status',
              'search',
              'source-views',
            ].includes(id);
            const needsNetwork = [
              'copy-feed',
              'add-feed',
              'refresh-feed',
            ].includes(id);
            const hasFeeds =
              !['refresh-feed', 'remove-feed', 'toggle-feed'].includes(id) ||
              view.calendarIcsSubscriptions$.value.length > 0;
            const reason = !sourceAvailable
              ? 'calendar.chrome.unavailable'
              : writes && readonly
                ? 'calendar.chrome.readonly'
                : needsNetwork && !network
                  ? 'calendar.chrome.offline'
                  : !hasFeeds
                    ? 'calendar.chrome.unavailable'
                    : null;
            return {
              id,
              enabled: reason === null,
              pending: this.pendingCommands.value.has(id),
              disabledReason: reason ? dataViewI18nT(reason) : null,
            };
          }),
        },
        sourceTools: html`<strong>${view.name$.value}</strong>`,
        hostControls: nothing,
        onToday: () => this.setCalendarCursor(new Date()),
        onPrevious: () => this.shiftCursor(-1),
        onNext: () => this.shiftCursor(1),
        onModeChange: mode => {
          this.resetAllDayRenderLimit();
          if (this.rangeIndicatorCursor.value)
            this.setCalendarCursor(this.rangeIndicatorCursor.value);
          view.setCalendarMode(mode);
        },
        onDisplayChange: settings => view.setDisplaySettings(settings),
        onFunction: id => this.runChromeFunction(id),
      })}<input
        data-calendar-file-input
        hidden
        style="display: none"
        class=${styles.fileInput}
        type="file"
        accept=".ics,text/calendar"
        @change=${(change: Event) => {
          const input = change.currentTarget as HTMLInputElement;
          const file = input.files?.[0];
          input.value = '';
          const session = this.importSession;
          this.importSession = undefined;
          if (file && session)
            void this.runSourceCommand(
              () => session.importIcs(file),
              'import-ics'
            );
        }}
      />`;
  }

  private runChromeFunction(id: CalendarFunctionId): void {
    const session = this.commandSession;
    const anchor = this.querySelector<HTMLElement>(
      `[data-calendar-function="${id}"]`
    );
    if (!session || !anchor) {
      this.calendarError.value = dataViewI18nT('calendar.chrome.unavailable');
      return;
    }
    this.sourcePopup?.close();
    this.sourcePopup = undefined;
    const fail = (error: unknown) => {
      if (this.commandSession === session)
        this.calendarError.value =
          error instanceof CalendarCommandError
            ? error.message
            : dataViewI18nT('calendar.chrome.operationFailed');
    };
    try {
      switch (id) {
        case 'create':
          this.detailReturnFocus = anchor;
          this.createEvent();
          break;
        case 'export-ics':
          void this.runSourceCommand(
            async current =>
              downloadCalendarIcs(
                renderCalendarEventsIcs(
                  await current.exportEvents(this.visibleWindow())
                ),
                'streamlain-calendar.ics'
              ),
            id
          );
          break;
        case 'import-ics':
          this.importSession = session;
          this.querySelector<HTMLInputElement>(
            '[data-calendar-file-input]'
          )!.click();
          break;
        case 'copy-feed':
          void this.runSourceCommand(current => current.copyFeedLink(), id);
          break;
        case 'add-feed':
          this.icsFeedFormOpen.value = !this.icsFeedFormOpen.value;
          this.queueIntrinsicContentHeightChange();
          break;
        case 'refresh-feed':
        case 'remove-feed':
        case 'toggle-feed':
          this.sourcePopup = openCalendarSourceOverlays(
            session,
            this.visibleWindow(),
            anchor,
            this.logic.view.readonly$.value,
            fail
          );
          break;
        case 'filter':
        case 'sort':
        case 'search':
          this.sourcePopup = openCalendarSourceTool(
            session,
            id,
            anchor,
            this.logic.eventTrace
          );
          break;
        case 'setup':
          this.openWizard();
          break;
        case 'complete-fields':
          if (!this.logic.view.readonly$.value)
            this.logic.view.ensureCalendarColumns();
          break;
        case 'rename-source':
          this.sourcePopup = openCalendarSourceRename(
            session,
            anchor,
            async title => session.view.nameSet(title),
            fail
          );
          break;
        case 'source-views':
          this.sourcePopup = openCalendarSourceViews(session, anchor, viewId =>
            session.view.manager.setCurrentView(viewId)
          );
          break;
        case 'mailbox-status':
          this.sourcePopup = animateCalendarSourcePopup(
            popMenu(popupTargetFromElement(anchor), {
              options: {
                title: { text: session.view.name$.value },
                items: [
                  () =>
                    html`<span
                      >${dataViewI18nT(
                        this.mailboxLinkLoadFailed.value
                          ? 'calendar.mailboxLink.unavailable'
                          : hasActiveOutboundMailboxLink(
                                this.mailboxLinks.value
                              )
                            ? 'calendar.mailboxLink.outboundLinked'
                            : 'calendar.mailboxLink.notLinked'
                      )}</span
                    >`,
                ],
              },
            })
          );
          break;
      }
    } catch (error) {
      fail(error);
    }
  }

  private renderIcsFeedForm(): TemplateResult | typeof nothing {
    const close = () => {
      this.icsFeedFormOpen.value = false;
      this.icsFeedUrlDraft.value = '';
      this.queueIntrinsicContentHeightChange();
    };
    return renderCalendarIcsFeedForm({
      onCancel: close,
      onInput: value => (this.icsFeedUrlDraft.value = value),
      onSubmit: url =>
        void this.runSourceCommand(session => session.addFeed(url)),
      open: this.icsFeedFormOpen.value,
      readonly: this.logic.view.readonly$.value,
      url: this.icsFeedUrlDraft.value,
    });
  }

  private renderOptionalFieldsNotice(): TemplateResult | typeof nothing {
    const missing = this.logic.view.optionalMissingColumnRoles$.value;
    if (!missing.length) return nothing;
    return html`<div class="${styles.notice}">
      ${dataViewI18nT('calendar.notice.missingFields', {
        fields: missing
          .map(role =>
            dataViewI18nT(
              `calendar.wizard.role.${role}.label` as DataViewI18nKey
            )
          )
          .join(', '),
      })}
      <button
        class="${styles.inlineTextButton}"
        aria-label=${dataViewI18nT('calendar.action.completeFields')}
        ?disabled=${this.logic.view.readonly$.value}
        @click=${() => {
          this.logic.view.ensureCalendarColumns();
          this.requestUpdate();
        }}
      >
        ${dataViewI18nT('calendar.action.completeFields')}
      </button>
    </div>`;
  }

  private renderProjectionNotice(): TemplateResult | typeof nothing {
    const noticeKey = this.projectionNoticeKey$.value;
    if (!noticeKey || noticeKey === 'calendar.notice.partialProjection') {
      return nothing;
    }
    return html`<div
      class="${styles.notice}"
      data-data-view-body-render-content="true"
    >
      ${dataViewI18nT(noticeKey)}
    </div>`;
  }

  private gridRenderContext(): CalendarGridRenderContext {
    const view = this.logic.view;
    const displayCursor = this.rangeIndicatorCursor.value ?? this.cursor.value;
    return {
      allDayCollapsed: this.allDayCollapsed.value,
      allDayRenderLimit: this.allDayRenderLimit.value,
      allDayEventsForDays: days => this.allDayEventsForDays(days),
      calendarIds: this.calendarIds$.value,
      cursor: displayCursor,
      displaySettings: this.logic.view.displaySettings$.value,
      dragCreateSlot: this.dragCreateSlot.value,
      draggedOccurrence: this.draggedOccurrence.value,
      draggedOccurrenceOffset: this.draggedOccurrenceOffset.value,
      eventEditPreview: this.eventEditPreview.value,
      eventsForDay: day => this.eventsForDay(day),
      finishSlotDrag: (day, endMinute) => this.finishSlotDrag(day, endMinute),
      mode: view.calendarMode$.value,
      now: this.now.value,
      onClearDragCreateSlot: () => {
        this.dragCreateSlot.value = undefined;
      },
      onCreateEvent: day => this.createEvent(day),
      onDragCreateSlot: (day, startMinute) => {
        this.dragCreateSlot.value = {
          allDay: view.calendarMode$.value === 'month',
          day,
          endDay: day,
          endMinute: startMinute,
          startMinute,
        };
      },
      onPreviewDragCreateSlot: (day, endMinute) => {
        const slot = this.dragCreateSlot.value;
        if (!slot) return;
        this.dragCreateSlot.value = { ...slot, endDay: day, endMinute };
      },
      onDraggedOccurrenceChange: (occurrence, offset) => {
        this.draggedOccurrence.value = occurrence;
        this.draggedOccurrenceOffset.value = occurrence
          ? (offset ?? { x: 0, y: 0 })
          : undefined;
      },
      onEventContextMenu: (contextMenu, event) =>
        this.openCalendarEventContextMenu(contextMenu, event),
      onEventKeyDown: (key, event) =>
        handleCalendarEventKeyDown(this.interactionContext(), key, event),
      onClearOccurrencePreview: this.clearCalendarEventEditPreview,
      onCommitOccurrenceMove: (event, day, startMinute) => {
        const next = this.resolveCalendarOccurrenceMove(
          event,
          day,
          startMinute
        );
        this.commitCalendarEventEdit(event, next.startAt, next.endAt, {
          allDay: false,
        });
      },
      onCommitOccurrenceMoveToDay: (event, day) => {
        const next = this.resolveCalendarOccurrenceDayMove(event, day);
        this.commitCalendarEventEdit(event, next.startAt, next.endAt, {
          allDay: event.source.allDay,
        });
      },
      onCommitOccurrenceMoveToAllDay: (event, day) => {
        const next = this.resolveCalendarOccurrenceAllDayMove(event, day);
        this.commitCalendarEventEdit(event, next.startAt, next.endAt, {
          allDay: true,
        });
      },
      onMoveOccurrenceToDay: (event, day) =>
        moveCalendarOccurrenceToDay(this.interactionContext(), event, day),
      onOpenDay: day => {
        this.setCalendarCursor(day);
        view.setCalendarMode('day');
      },
      onOpenWeek: day => {
        this.setCalendarCursor(day);
        view.setCalendarMode('week');
      },
      onRefreshIcsSubscription: subscription =>
        void this.runSourceCommand(session =>
          session.refreshFeed(subscription.id)
        ),
      onRemoveIcsSubscription: subscriptionId =>
        void this.runSourceCommand(session =>
          session.removeFeed(subscriptionId)
        ),
      onSelectOccurrence: event => this.selectOccurrence(event),
      onShowMoreAllDayEvents: total => {
        this.allDayRenderLimit.value = nextCalendarAllDayRenderLimit(
          this.allDayRenderLimit.value,
          total
        );
        this.queueIntrinsicContentHeightChange();
      },
      onRenameOccurrence: (event, title) => this.renameOccurrence(event, title),
      onToggleAllDayCollapsed: () => {
        this.allDayCollapsed.value = !this.allDayCollapsed.value;
        this.queueIntrinsicContentHeightChange();
      },
      onSetIcsSubscriptionEnabled: (subscriptionId, enabled) =>
        void this.runSourceCommand(session =>
          session.setFeedEnabled(subscriptionId, enabled)
        ),
      onSetVisibleCalendarIds: ids => view.setVisibleCalendarIds(ids),
      onJumpToHiddenTimedEvents: this.jumpToHiddenTimedEvents,
      onPreviewOccurrenceMove: (event, day, startMinute) =>
        this.previewCalendarOccurrenceMove(event, day, startMinute),
      onPreviewOccurrenceMoveToDay: (event, day) =>
        this.previewCalendarOccurrenceMoveToDay(event, day),
      onPreviewOccurrenceMoveToAllDay: (event, day) =>
        this.previewCalendarOccurrenceMoveToAllDay(event, day),
      onPreviewOccurrenceResize: (event, startAt, endAt) =>
        this.previewCalendarEventEdit(event, startAt, endAt, {
          allDay: event.source.allDay,
        }),
      onCommitOccurrenceResize: (event, startAt, endAt) =>
        this.commitCalendarEventEdit(event, startAt, endAt, {
          allDay: event.source.allDay,
        }),
      onStartResizePointer: (pointer, event, edge) =>
        startCalendarOccurrenceResizePointer(
          this.interactionContext(),
          pointer,
          event,
          edge
        ),
      rangeLoading: this.rangeLoading.value,
      readonly: view.readonly$.value,
      recurrenceErrors: this.recurrenceErrors$.value,
      selectedOccurrenceStart: this.selectedOccurrenceStart.value,
      selectedRowId: this.selectedRowId.value,
      subscriptionStates: this.subscriptionStates.value,
      subscriptions: view.calendarIcsSubscriptions$.value,
      timedOffscreen: this.timedOffscreen.value,
      timedRenderWindow: this.timedRenderWindow.value,
      visibleCalendarIds: view.visibleCalendarIds$.value,
    };
  }

  private selectOccurrence(event: CalendarOccurrence<CalendarEvent>): void {
    if (
      this.selectedRowId.value &&
      this.selectedRowId.value !== event.source.rowId
    ) {
      void this.leaveSelectedEvent(() => this.selectOccurrence(event));
      return;
    }
    const trigger = [
      ...this.querySelectorAll<HTMLElement>('[data-calendar-event-key]'),
    ].find(element => element.dataset.calendarEventKey === event.source.rowId);
    this.detailReturnFocus =
      trigger ??
      (this.ownerDocument.activeElement instanceof HTMLElement
        ? this.ownerDocument.activeElement
        : null);
    this.setOccurrenceSelection(
      event.source.rowId,
      event.startAt.toISOString()
    );
  }

  private setOccurrenceSelection(
    rowId: string | undefined,
    occurrenceStart: string | undefined
  ): void {
    this.selectedRowId.value = rowId;
    this.selectedOccurrenceStart.value = occurrenceStart;
    if (!rowId) return;
    this.logic.setSelection({
      rowId,
      selectionType: 'event',
      type: 'calendar',
      viewId: this.logic.view.id,
    });
  }

  private interactionContext(): CalendarEventInteractionContext {
    return {
      addRow: () => this.logic.addRow('end'),
      clearCalendarEventEditPreview: this.clearCalendarEventEditPreview,
      commitCalendarEventEdit: this.commitCalendarEventEdit,
      deleteEvent: rowId => void this.deleteEvent(rowId),
      requestDeleteEvent: rowId => this.requestDeleteEvent(rowId),
      confirmationOpen: () =>
        this.deleteConfirmationOpen || this.leavePromptOpen,
      previewCalendarEventEdit: this.previewCalendarEventEdit,
      readonly: () => this.logic.view.readonly$.value,
      setCalendarError: message => {
        this.calendarError.value = message;
      },
      setCellJson: (rowId, propertyId, value) =>
        this.setCellJson(rowId, propertyId, value),
      setSelectedOccurrence: (rowId, occurrenceStart) =>
        this.setOccurrenceSelection(rowId, occurrenceStart),
      view: this.logic.view,
    };
  }

  private openCalendarClipboardMenu(
    target: HTMLElement,
    event?: CalendarOccurrence<CalendarEvent>
  ): void {
    const primaryModifier =
      typeof navigator !== 'undefined' &&
      /Mac|iPhone|iPad/u.test(navigator.platform)
        ? '⌘'
        : 'Ctrl+';
    const readonly =
      this.logic.view.readonly$.value ||
      event?.source.externalReadonly === true;
    popFilterableSimpleMenu(popupTargetFromElement(target), [
      ...(event
        ? [
            menu.action({
              name: dataViewI18nT('calendar.action.copy'),
              prefix: CopyIcon(),
              postfix: html`<span>${primaryModifier}C</span>`,
              select: () => copyCalendarOccurrence(event),
            }),
            menu.action({
              name: dataViewI18nT('calendar.action.cut'),
              disabled: readonly,
              postfix: html`<span>${primaryModifier}X</span>`,
              select: () => {
                if (readonly) return false;
                cutCalendarOccurrence(
                  event,
                  () => void this.deleteEvent(event.source.rowId)
                );
                return;
              },
            }),
          ]
        : []),
      menu.action({
        name: dataViewI18nT('calendar.action.paste'),
        disabled: readonly || !calendarClipboardHasEvent(),
        prefix: PasteIcon(),
        postfix: html`<span>${primaryModifier}V</span>`,
        select: () => {
          if (readonly || !calendarClipboardHasEvent()) return false;
          void pasteCalendarOccurrence(this.interactionContext());
          return;
        },
      }),
    ]);
  }

  private openCalendarEventContextMenu(
    contextMenu: MouseEvent,
    event: CalendarOccurrence<CalendarEvent>
  ): void {
    contextMenu.preventDefault();
    contextMenu.stopPropagation();
    this.selectOccurrence(event);
    this.openCalendarClipboardMenu(
      contextMenu.currentTarget as HTMLElement,
      event
    );
  }

  private readonly onCalendarSurfaceContextMenu = (
    contextMenu: MouseEvent
  ): void => {
    const target = contextMenu.target as HTMLElement;
    if (!target.closest('[data-calendar-surface="true"]')) return;
    if (target.closest('[data-calendar-event="true"]')) return;
    contextMenu.preventDefault();
    this.openCalendarClipboardMenu(target);
  };

  private readonly onCalendarRootKeyDown = (key: KeyboardEvent): void => {
    // Typing in a calendar field belongs to the field. The editor's keyboard
    // dispatcher listens on document and would run block hotkeys otherwise.
    if (isEditableKeyboardEventTarget(key)) {
      key.stopPropagation();
      return;
    }
    const rowId = this.selectedRowId.value;
    const event = rowId ? readCalendarEvent(this.logic.view, rowId) : undefined;
    if (
      handleCalendarDeleteKey({
        key,
        selection: event
          ? {
              sourceId: this.logic.view.id,
              rowId: event.rowId,
              occurrenceStart: this.selectedOccurrenceStart.value ?? null,
              readonly: this.logic.view.readonly$.value,
              externalReadonly: event.externalReadonly === true,
            }
          : null,
        confirmationOpen: this.deleteConfirmationOpen || this.leavePromptOpen,
        requestDelete: target => this.requestDeleteEvent(target.rowId),
      })
    )
      return;
    if (key.defaultPrevented) return;
    if (!(key.metaKey || key.ctrlKey) || key.key.toLowerCase() !== 'v') return;
    const target = key.target as HTMLElement;
    if (
      target.matches('input, textarea, select') ||
      target.isContentEditable ||
      !calendarClipboardHasEvent()
    ) {
      return;
    }
    key.preventDefault();
    void pasteCalendarOccurrence(this.interactionContext());
  };

  private icsContext(): CalendarIcsActionsContext {
    return {
      signal: this.icsAbort.signal,
      addRow: () => this.logic.addRow('end'),
      closeFeedForm: () => {
        this.icsFeedFormOpen.value = false;
        this.icsFeedUrlDraft.value = '';
      },
      getSubscriptionEvents: () => this.subscriptionEvents.value,
      getSubscriptionLoadKey: () => this.subscriptionLoadKey,
      getSubscriptionStates: () => this.subscriptionStates.value,
      queueIntrinsicContentHeightChange: () =>
        this.queueIntrinsicContentHeightChange(),
      readonly: () => this.logic.view.readonly$.value,
      requestCalendarUpdate: () => this.requestUpdate(),
      setCalendarError: message => {
        this.calendarError.value = message;
      },
      setCellJson: (rowId, propertyId, value) =>
        this.setCellJson(rowId, propertyId, value),
      setSubscriptionEvents: events => {
        this.subscriptionEvents.value = events;
      },
      setSubscriptionLoadKey: key => {
        this.subscriptionLoadKey = key;
      },
      setSubscriptionStates: states => {
        this.subscriptionStates.value = states;
      },
      view: this.logic.view,
    };
  }

  private async runSourceCommand(
    command: (session: CalendarCommandSession) => Promise<void> | void,
    key?: CalendarFunctionId
  ): Promise<void> {
    const session = this.commandSession;
    if (!session) {
      this.calendarError.value = dataViewI18nT('calendar.chrome.unavailable');
      return;
    }
    if (key) {
      this.pendingCommands.value = new Set([
        ...this.pendingCommands.value,
        key,
      ]);
    }
    try {
      await command(session);
    } catch (error) {
      if (session === this.commandSession)
        this.calendarError.value =
          error instanceof CalendarCommandError
            ? error.message
            : dataViewI18nT('calendar.chrome.operationFailed');
    } finally {
      if (key && session === this.commandSession)
        this.pendingCommands.value = new Set(
          [...this.pendingCommands.value].filter(value => value !== key)
        );
    }
  }

  private async reconcileEventSideEffects(
    event: CalendarEvent,
    options: { skipMail?: boolean } = {}
  ): Promise<void> {
    if (this.logic.view.readonly$.value || event.externalReadonly) return;
    try {
      validateCalendarRecurrenceRule(event.recurrence);
    } catch (error) {
      this.calendarError.value =
        error instanceof Error
          ? error.message
          : dataViewI18nT('calendar.error.recurrenceInvalid');
      this.setReconcileState(event.rowId, 'error');
      return;
    }
    const context = resolveCalendarReconcileContext(
      this.logic.view.manager.dataSource
    );
    const columns = calendarReconcileColumns(this.logic.view);
    if (!context || !columns) {
      this.setReconcileState(event.rowId, 'error');
      return;
    }

    const snapshot = toCalendarReconcileSnapshot(
      event,
      context,
      this.logic.view.id
    );
    this.setReconcileState(event.rowId, 'syncing');

    try {
      const result = await this.submitEventReconcile({
        columns,
        context,
        previousEvent: this.reconcileSnapshots.get(event.rowId) ?? null,
        rowId: event.rowId,
        skipMail: options.skipMail === true,
        viewId: this.logic.view.id,
      });
      this.reconcileSnapshots.set(event.rowId, snapshot);
      this.setReconcileState(
        event.rowId,
        result === 'pending' ? 'pending' : 'synced'
      );
      this.calendarError.value =
        result === 'pending'
          ? undefined
          : options.skipMail === true && calendarEventHasMailRecipients(event)
            ? dataViewI18nT('calendar.notice.savedWithoutMail')
            : undefined;
    } catch (error) {
      console.error('[calendar] reconcile failed', error);
      this.calendarError.value = dataViewI18nT('calendar.error.updatesFailed');
      this.setReconcileState(event.rowId, 'error');
    }
  }

  private async confirmCalendarMail(
    event: CalendarEvent,
    kind: 'cancel' | 'update'
  ): Promise<boolean> {
    if (!calendarEventHasMailRecipients(event)) {
      return false;
    }
    // Mailbox configuration can change outside this mounted view. Refresh the
    // owning backend decision before asking the user to send updates or mail.
    await this.loadMailboxLinks(
      resolveCalendarReconcileContext(this.logic.view.manager.dataSource)
    );
    const sendEligibility = calendarMailboxSendEligibility(
      this.mailboxLinks.value,
      this.mailboxLinkLoadFailed.value
    );
    if (!sendEligibility.canSend) {
      const message = calendarMailboxSendEligibilityMessage(sendEligibility);
      this.calendarError.value = message;
      return false;
    }
    const message =
      kind === 'cancel'
        ? dataViewI18nT('calendar.confirm.sendCancellation')
        : dataViewI18nT('calendar.confirm.sendUpdate');
    const recipients = parseCalendarAttendees(event.attendees)
      .map(attendee => attendee.name || attendee.email || attendee.key)
      .join(', ');
    return this.openConfirmModal({
      title: message,
      description: recipients,
      confirmText:
        kind === 'cancel'
          ? dataViewI18nT('calendar.action.sendCancellation')
          : dataViewI18nT('calendar.action.sendUpdates'),
      cancelText: dataViewI18nT('calendar.action.notNow'),
    });
  }

  /** Resolves true on confirm; the host registers the modal bridge. */
  private openConfirmModal(options: {
    cancelText: string;
    confirmText: string;
    description: string;
    destructive?: boolean;
    title: string;
  }): Promise<boolean> {
    const modal = (
      window as Window & {
        __confirmModal?: {
          openConfirmModal(options: {
            cancelText: string;
            confirmButtonOptions?: { variant: 'error' };
            confirmText: string;
            description: string;
            onCancel: () => void;
            onConfirm: () => void;
            title: string;
          }): void;
        };
      }
    ).__confirmModal;
    if (!modal) {
      console.warn('[calendar] __confirmModal bridge not available');
      return Promise.resolve(false);
    }
    const { destructive, ...text } = options;
    return new Promise<boolean>(resolve => {
      modal.openConfirmModal({
        ...text,
        ...(destructive ? { confirmButtonOptions: { variant: 'error' } } : {}),
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  }

  /** All explicit deletes use this dialog and retain the exact source view until completion. */
  private async requestDeleteEvent(rowId: string): Promise<void> {
    const view = this.logic.view;
    const event = readCalendarEvent(view, rowId, { includeDetails: true });
    if (
      this.deleteConfirmationOpen ||
      this.leavePromptOpen ||
      view.readonly$.value ||
      !event ||
      event.externalReadonly
    )
      return;
    this.deleteConfirmationOpen = true;
    try {
      const confirmed = await this.openConfirmModal({
        title: dataViewI18nT('calendar.accessibility.deleteEvent'),
        description: event.title || dataViewI18nT('calendar.event.untitled'),
        confirmText: dataViewI18nT('calendar.action.delete'),
        cancelText: dataViewI18nT('calendar.action.cancel'),
        destructive: true,
      });
      if (confirmed) await this.deleteEvent(rowId, view);
    } catch (error) {
      console.error('[calendar] delete failed', error);
      this.calendarError.value = dataViewI18nT(
        'calendar.error.deleteReconcile'
      );
    } finally {
      this.deleteConfirmationOpen = false;
    }
  }

  private async sendEventUpdates(event: CalendarEvent): Promise<boolean> {
    const promptWasAlreadyOpen = this.leavePromptOpen;
    this.leavePromptOpen = true;
    try {
      if (!(await this.confirmCalendarMail(event, 'update'))) return false;
      await this.reconcileEventSideEffects(event);
      if (this.reconcileState.value[event.rowId] === 'synced') {
        this.dirtyEventRows.delete(event.rowId);
        return true;
      }
      return false;
    } finally {
      if (!promptWasAlreadyOpen) {
        this.leavePromptOpen = false;
      }
    }
  }

  private async persistEventBeforeLeave(event: CalendarEvent): Promise<void> {
    if (calendarEventHasMailRecipients(event)) {
      await this.sendEventUpdates(event);
      return;
    }
    await this.reconcileEventSideEffects(event, { skipMail: true });
    const state = this.reconcileState.value[event.rowId];
    if (state === 'synced' || state === 'pending') {
      this.dirtyEventRows.delete(event.rowId);
    }
  }

  private async leaveSelectedEvent(leave: () => void): Promise<void> {
    const rowId = this.selectedRowId.value;
    const event = rowId ? readCalendarEvent(this.logic.view, rowId) : null;
    if (event && this.dirtyEventRows.has(event.rowId)) {
      if (this.leavePromptOpen) return;
      this.leavePromptOpen = true;
      try {
        await this.persistEventBeforeLeave(event);
      } finally {
        this.leavePromptOpen = false;
      }
    }
    leave();
  }

  private readonly onDocumentClickCapture = (event: MouseEvent): void => {
    if (
      this.bypassExternalLeavePrompt ||
      this.leavePromptOpen ||
      !this.selectedRowId.value ||
      event.composedPath().includes(this)
    ) {
      return;
    }
    const rowId = this.selectedRowId.value;
    const selectedEvent = readCalendarEvent(this.logic.view, rowId);
    if (!selectedEvent || !this.dirtyEventRows.has(rowId)) {
      return;
    }
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    // Dialogs and other portalled overlays are part of the active interaction,
    // even though they render outside this Lit element. Treating their buttons
    // as navigation away intercepts the confirmation click and recursively
    // opens another leave prompt.
    if (target.closest('[role="dialog"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    void this.leaveSelectedEvent(() => {
      this.bypassExternalLeavePrompt = true;
      try {
        target.click();
      } finally {
        this.bypassExternalLeavePrompt = false;
      }
    });
  };

  private setReconcileState(
    rowId: string,
    state: CalendarReconcileState
  ): void {
    this.reconcileState.value = {
      ...this.reconcileState.value,
      [rowId]: state,
    };
  }

  private async submitEventReconcile(
    input: {
      columns: NonNullable<ReturnType<typeof calendarReconcileColumns>>;
      context: NonNullable<ReturnType<typeof resolveCalendarReconcileContext>>;
      previousEvent: CalendarReconcileSnapshot | null;
      rowId: string;
      skipMail: boolean;
      viewId: string;
    },
    view: CalendarSingleView = this.logic.view
  ): Promise<'completed' | 'pending'> {
    const service = resolveCalendarReconcileService(view.manager.dataSource);
    if (service) {
      return service.submit(input);
    }
    await submitCalendarReconcile({
      ...input,
      flushDocs: flushCalendarReconcileDocs,
    });
    return 'completed';
  }

  private async deleteEvent(
    rowId: string,
    view: CalendarSingleView = this.logic.view
  ): Promise<void> {
    if (view.readonly$.value) return;
    const context = resolveCalendarReconcileContext(view.manager.dataSource);
    const columns = calendarReconcileColumns(view);
    const event = readCalendarEvent(view, rowId, { includeDetails: true });
    if (!event || event.externalReadonly) return;
    const previousEvent =
      (view === this.logic.view ? this.reconcileSnapshots.get(rowId) : null) ??
      (event && context
        ? toCalendarReconcileSnapshot(event, context, view.id)
        : null);
    const sendMail = await this.confirmCalendarMail(event, 'cancel');
    if (
      view.readonly$.value ||
      readCalendarEvent(view, rowId)?.externalReadonly
    )
      return;
    view.rowsDelete([rowId]);
    if (view === this.logic.view) {
      this.reconcileSnapshots.delete(rowId);
      this.selectedRowId.value = undefined;
      this.selectedOccurrenceStart.value = undefined;
      this.dispatchExternalDetailClose();
    }
    this.requestUpdate();
    this.queueIntrinsicContentHeightChange();

    if (!context || !columns) {
      this.calendarError.value = dataViewI18nT(
        'calendar.error.deleteReconcile'
      );
      return;
    }

    try {
      const result = await this.submitEventReconcile(
        {
          columns,
          context,
          previousEvent,
          rowId,
          skipMail: !sendMail,
          viewId: view.id,
        },
        view
      );
      this.calendarError.value =
        result === 'pending'
          ? dataViewI18nT('calendar.notice.savedOffline')
          : undefined;
    } catch (error) {
      console.error('[calendar] delete reconcile failed', error);
      this.calendarError.value = dataViewI18nT(
        'calendar.error.deleteReconcile'
      );
    }
  }

  private renderDetails(): TemplateResult | typeof nothing {
    const rowId = this.selectedRowId.value;
    if (!rowId) return nothing;
    const occurrence =
      this.events$.value.find(
        item =>
          item.source.rowId === rowId &&
          item.startAt.toISOString() === this.selectedOccurrenceStart.value
      ) ?? this.events$.value.find(item => item.source.rowId === rowId);
    const event =
      readCalendarEvent(this.logic.view, rowId) ?? occurrence?.source;
    if (!event) return nothing;
    const view = this.logic.view;
    return renderCalendarDetails({
      cancelOccurrence: item =>
        void cancelCalendarOccurrence(this.interactionContext(), item),
      close: () => {
        this.dispatchExternalDetailClose();
        void this.leaveSelectedEvent(() => {
          this.selectedRowId.value = undefined;
          this.selectedOccurrenceStart.value = undefined;
        });
      },
      deleteEvent: targetRowId => void this.requestDeleteEvent(targetRowId),
      detachOccurrence: item =>
        void detachCalendarOccurrence(this.interactionContext(), item),
      downloadEventIcs: item =>
        downloadCalendarEventIcs(this.icsContext(), item),
      event,
      events: this.events$.value,
      occurrence,
      reconcileEventSideEffects: item => void this.sendEventUpdates(item),
      recurrenceError: this.recurrenceErrors$.value.get(rowId),
      rowId,
      setCalendarError: message => (this.calendarError.value = message),
      setCellDraftJson: (
        targetRowId: string,
        propertyId: string | undefined,
        value: unknown
      ) => this.setCellJson(targetRowId, propertyId, value),
      setCellJson: (
        targetRowId: string,
        propertyId: string | undefined,
        value: unknown
      ) => this.setCellJson(targetRowId, propertyId, value),
      commitCellChanges: () => {},
      hasPendingMailUpdates:
        this.dirtyEventRows.has(rowId) && calendarEventHasMailRecipients(event),
      syncState: this.reconcileState.value[rowId] ?? 'idle',
      view,
    });
  }

  override render(): TemplateResult {
    return html`
      <div
        class="${setupWizardBodyHostStyle} ${styles.root}"
        data-data-view-body="true"
        data-data-view-body-state=${dataViewBodyStateValue(this.getBodyState())}
        @contextmenu=${this.onCalendarSurfaceContextMenu}
        @keydown=${this.onCalendarRootKeyDown}
      >
        ${this.renderBodyContent()}
      </div>
    `;
  }

  private renderBodyContent(): TemplateResult {
    return renderSetupWizardBodyContent({
      configured: this.isConfigured(),
      renderConfigured: () => this.renderConfigured(),
      renderEmptyState: () => this.renderEmptyState(),
      renderSetupWizard: () => this.renderSetupWizard(),
      wizardOpen: this.wizardOpen.value,
    });
  }

  private renderConfigured(): TemplateResult {
    const details = this.renderDetails();
    if (this.externalDetailOnly) {
      return html`
        <div
          class="${styles.configuredBody}"
          data-calendar-detail-only="true"
          data-data-view-body-content="true"
        >
          <div
            class="${styles.content}"
            data-calendar-detail-only="true"
            data-data-view-body-intrinsic="true"
            data-has-details=${details !== nothing}
          >
            ${details}
          </div>
        </div>
      `;
    }
    const calendarError = this.calendarError.value;
    return html`
      <div class="${styles.configuredBody}" data-data-view-body-content="true">
        <div
          class="${styles.configuredToolbar}"
          data-data-view-body-intrinsic="true"
        >
          ${this.renderToolbar()} ${this.renderIcsFeedForm()}
        </div>
        <div class="${styles.content}" data-data-view-body-intrinsic="true">
          ${renderCalendarSurface({
            beforeGrid: html`${calendarError
              ? html`<div class="${styles.conflict}">${calendarError}</div>`
              : nothing}
            ${this.renderOptionalFieldsNotice()}
            ${this.renderProjectionNotice()}`,
            context: this.gridRenderContext(),
            days: this.visibleDays(),
          })}
          <div data-calendar-detail-shell-mount></div>
        </div>
      </div>
    `;
  }

  private renderEmptyState(): TemplateResult {
    const view = this.logic.view;
    const deleted =
      !view.isConfigured$.value &&
      view.requiredMissingColumnRoles$.value.length > 0 &&
      Boolean(view.startDateCol$.value || view.endDateCol$.value);
    return html`<div
      class="${styles.emptyState}"
      data-data-view-body-content="true"
      data-data-view-body-intrinsic="true"
      data-data-view-body-render-content="true"
      data-data-view-body-render-root="true"
    >
      <div class="${styles.emptyStateTitle}">
        ${deleted
          ? dataViewI18nT('calendar.empty.deletedTitle')
          : dataViewI18nT('calendar.empty.title')}
      </div>
      <div class="${styles.emptyStateText}">
        ${deleted
          ? dataViewI18nT('calendar.empty.deletedDescription')
          : dataViewI18nT('calendar.empty.description')}
      </div>
      <button
        class="${styles.emptyStateCta}"
        aria-label=${deleted
          ? dataViewI18nT('calendar.empty.deletedTitle')
          : dataViewI18nT('calendar.empty.setupCta')}
        ?disabled=${view.readonly$.value}
        @click=${this.openWizard}
      >
        ${deleted
          ? dataViewI18nT('calendar.empty.deletedTitle')
          : dataViewI18nT('calendar.empty.setupCta')}
      </button>
    </div>`;
  }

  private renderSetupWizard(): TemplateResult {
    return html`<dv-calendar-setup-wizard
      class=${styles.calendarSetupWizardInline}
      .contained=${false}
      .inline=${true}
      .view=${this.logic.view}
      .onClose=${this.closeWizard}
      .strings=${this.getWizardStrings()}
      @calendar-setup-wizard-layout-change=${() => {
        this.queueIntrinsicContentHeightChange();
      }}
    ></dv-calendar-setup-wizard>`;
  }

  private getWizardStrings(): Partial<CalendarWizardStrings> {
    const win = window as Window & {
      __streamlainHooks?: {
        calendarWizardStrings?: Partial<CalendarWizardStrings>;
      };
    };
    return win.__streamlainHooks?.calendarWizardStrings ?? {};
  }
}
